import React, { useRef } from "react";
import { type FormikProps } from "formik";
import { isEmpty } from "lodash";

import UploadPdfField from "./UploadPdfField";
import EndpointsToGetSasLink from "../../../../../enums/endpointsToGetSasLink";

import { ValidatedForm } from "../../../../../components/forms";
import { type PdfFormFields, type PdfFormProps } from "../types";
import { type FileLike } from "interfaces";
import { nameofFactory } from "utils/nameof";
import { extractFormikProps } from "utils/formikUtils";
import { ViewContentLinkButton } from "components";
import { getContentLinkToEupFromPreview } from "utils/eupUtils";
import ImagePreview from "components/media/imagePreview/ImagePreview";
import {
  IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS,
  IMAGE_PREVIEW_PLACEHOLDER_VARIANTS,
} from "components/media/imagePreview/placeholders";

export type PdfFormPropsAll = FormikProps<PdfFormFields> & PdfFormProps;

const PdfForm = (props: PdfFormPropsAll) => {
  const { values, isReadOnlyView } = props;
  const descriptionHasInitialValue = useRef(!isEmpty(values.description));
  const formik = extractFormikProps<PdfFormFields, PdfFormPropsAll>(props);

  const onFileUpload = (fileName: string, uploadedPdfs: FileLike[], title: string, description: string) => {
    const newValues = { ...values, fileName, title, description, uploadedPdfs };
    props.setValues(newValues);
    props.onFileUpload(newValues);
  };

  const nameof = nameofFactory<PdfFormFields>();

  const onTitleBlur = (propertyName: string) => {
    const descriptionField = nameof("description");
    if (props.mode === "create" && !descriptionHasInitialValue.current && !props.touched[descriptionField]) {
      props.setFieldValue(descriptionField, values.title, true);
    }

    props.onBlur(propertyName);
  };

  const renderFields = (): React.ReactNode => {
    const isReadOnly = props.disabled || props.id <= 0;
    const firstUpload = props.id <= 0;

    return (
      <div>
        <UploadPdfField
          id={props.id}
          mode={props.mode}
          disabled={!!props.disabled}
          value={values.uploadedPdfs.length > 0 ? values.uploadedPdfs : [{ name: values.fileName } as FileLike]}
          formik={formik}
          firstUpload={firstUpload}
          onFileUpload={onFileUpload}
          isReadOnlyView={isReadOnlyView}
        />
        <ValidatedForm.InputField
          {...formik}
          label="Title"
          tooltip={{
            info: "This field is visible to end users.",
            width: 17,
          }}
          placeholder={"Enter PDF Title"}
          value={values.title}
          propertyName={nameof("title")}
          onBlur={onTitleBlur}
          markAsRequired
          disabled={isReadOnly}
          isReadOnlyView={isReadOnlyView}
        />
        <ValidatedForm.TextAreaField
          {...formik}
          tooltip={{
            info: "This field is visible to users and is displayed by the title.",
            width: 17,
          }}
          label="Description"
          placeholder={"Enter Description"}
          value={values.description}
          propertyName={nameof("description")}
          onBlur={props.onBlur}
          disabled={isReadOnly}
          markAsRequired
          isReadOnlyView={isReadOnlyView}
        />
        <ImagePreview
          {...formik}
          label="Thumbnail Image"
          value={values.thumbnailUrl}
          propertyName={nameof("thumbnailUrl")}
          endpointToGetSasLink={EndpointsToGetSasLink.Pdf(props.id)}
          customImagePreview
          onImageChanged={(_, imageName) => props.onImageChanged(imageName)}
          disabled={isReadOnly}
          combinedIcon
          placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.image}
          headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
          isReadOnlyView={isReadOnlyView}
          readOnlyEmptyState={"No thumbnail image uploaded"}
        />
      </div>
    );
  };

  const { isLoading, disablePreventTransitionPrompt } = props;
  return (
    <>
      {props.hasBeenPublished && (
        <ViewContentLinkButton href={getContentLinkToEupFromPreview["PDFs"](props.id)} fitted />
      )}
      <ValidatedForm
        dirty={props.dirty}
        unsavedChangesPrompt={{
          title: "Exit Without Saving?",
          message: "Are you sure you want to exit without saving this PDF? All information entered will be lost.",
        }}
        disablePreventTransitionPrompt={disablePreventTransitionPrompt}
        isLoading={isLoading}
        formWidthComputer={6}
        withoutFormik
      >
        {renderFields}
      </ValidatedForm>
    </>
  );
};

export default PdfForm;
